"use client";

import { useEffect, useRef } from "react";
import { useFormatter, useTranslations } from "use-intl";

import { CloseIcon } from "@/components/appIcons";
import type { EntityAboutSection } from "@/components/catalog-entity/entity-about-section";
import { cn } from "@/lib/utils";

interface EntityAboutPanelProps {
  panelId: string;
  entityName: string;
  sections: EntityAboutSection[];
  /** Open tab key, or `null` when the panel is closed. */
  activeKey: string | null;
  onTabChange: (key: string) => void;
  onClose: () => void;
}

/**
 * Side panel (desktop) / bottom sheet (mobile) with one tab per section. It is a
 * native `<dialog>` rendered on the server with every tab's content in the DOM,
 * so crawlers see the same links whether or not a visitor opens it.
 */
export function EntityAboutPanel({
  panelId,
  entityName,
  sections,
  activeKey,
  onTabChange,
  onClose,
}: EntityAboutPanelProps) {
  const t = useTranslations("shared.entityAbout");
  const ta = useTranslations("globals.accessibility");
  const format = useFormatter();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const shownKey = activeKey ?? sections[0]?.key;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (activeKey && !dialog.open) dialog.showModal();
    if (!activeKey && dialog.open) dialog.close();
  }, [activeKey]);

  useEffect(() => {
    if (!activeKey) return;
    const root = document.documentElement;
    const original = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      root.style.overflow = original;
    };
  }, [activeKey]);

  if (sections.length === 0) return null;

  return (
    <dialog
      ref={dialogRef}
      id={panelId}
      aria-label={t("panelLabel", { name: entityName })}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      className={cn(
        "m-0 hidden max-h-none max-w-none flex-col border-border bg-card p-0 text-foreground open:flex",
        "backdrop:bg-black/60",
        "fixed inset-x-0 top-auto bottom-0 h-[85dvh] w-full rounded-t-2xl border-t",
        "md:inset-y-0 md:right-0 md:left-auto md:h-dvh md:w-[35rem] md:rounded-none md:border-t-0 md:border-l",
      )}
    >
      <div
        aria-hidden="true"
        className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border md:hidden"
      />
      <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 md:px-6">
        <div
          role="tablist"
          aria-label={entityName}
          className="flex min-w-0 flex-1 gap-5 overflow-x-auto no-scrollbar"
        >
          {sections.map((section) => (
            <button
              key={section.key}
              type="button"
              role="tab"
              id={`${panelId}-tab-${section.key}`}
              aria-selected={shownKey === section.key}
              aria-controls={`${panelId}-${section.key}`}
              onClick={() => onTabChange(section.key)}
              className={cn(
                "-mb-px flex shrink-0 items-baseline gap-1.5 whitespace-nowrap border-b-2 py-3.5 text-sm font-semibold transition-colors",
                shownKey === section.key
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {section.label}
              <span className="text-xs font-medium text-muted-foreground">
                {format.number(section.count)}
              </span>
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-label={ta("close")}
          onClick={onClose}
          className="relative after:absolute after:-inset-0.5 after:content-[''] flex size-10 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <CloseIcon className="text-[18px]" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 md:px-6">
        {sections.map((section) => (
          <section
            key={section.key}
            role="tabpanel"
            id={`${panelId}-${section.key}`}
            aria-labelledby={`${panelId}-tab-${section.key}`}
            hidden={shownKey !== section.key}
          >
            {section.content}
          </section>
        ))}
      </div>
    </dialog>
  );
}
